// Scheletul clădirii — geometria placeholder-ului care ține locul modelului 3D
// cât timp rezultatul Pasului 7 se descarcă (v. ScheletIncarcare.tsx).
//
// Placeholder-ul e făcut din SILUETA proiectului (conturul nivelurilor, din
// soluția de arhitectură — același desen din care cardul de proiect își face
// miniatura) și e privit prin ACEEAȘI cameră ca modelul din Scene3D: aceeași
// direcție, aceeași distanță relativă la anvergură, același unghi de câmp. De
// asta, când modelul sosește, ia locul scheletului fără să sară — asta îl face
// placeholder, nu ilustrație.
//
// Totul de aici e pur (fără DOM), ca să se poată testa.

import type { Silueta } from "../api";
import type { StepRezumat, StepState } from "./tipuri";

export type Nivel = { poly: [number, number][]; z: number; h: number };
export type Punct = [number, number];

/** Nivelurile CORPULUI PRINCIPAL, de jos în sus. Structura se calculează pe
 *  un tronson (un corp), iar corpurile vin sortate după arie — cel mai mare e
 *  clădirea despre care e proiectul. */
export function nivelurileSiluetei(silueta: Silueta | null | undefined): Nivel[] {
  const corp = silueta?.corpuri?.find((c) => c.blocuri.length);
  if (!corp) return [];
  const out: Nivel[] = [];
  for (const b of corp.blocuri) {
    for (let j = 0; j < b.niveluri; j += 1) {
      out.push({ poly: b.poly, z: b.z + j * b.h, h: b.h });
    }
  }
  return out;
}

// --- camera: copia celei din Scene3D --------------------------------------
//
// Scene3D: PerspectiveCamera(45°, aspect), up = +z, poziția
// centru + anvergură·(0,9; −1,1; 0,75), țintă = centrul cutiei modelului,
// anvergura = latura MARE a cutiei (x, y sau z). Cifrele stau AICI o singură
// dată; dacă cineva mută camera scenei, testul din schelet.test.ts nu îl
// prinde — dar comentariul din StepFem trimite aici.
export const CAMERA = { fov_deg: 45, ofs: [0.9, -1.1, 0.75] as const };

export type Camera = {
  /** proiectează un punct 3D (în unitățile siluetei) în pixeli de ecran */
  P: (x: number, y: number, z: number) => Punct;
  /** distanța pătrată de la cameră la un punct — pentru ordinea de desen */
  adancime: (x: number, y: number, z: number) => number;
  /** poziția camerei, în unitățile siluetei */
  pozitie: [number, number, number];
};

export function camera(niveluri: Nivel[], w: number, h: number): Camera | null {
  if (!niveluri.length || w <= 0 || h <= 0) return null;
  const xs: number[] = [];
  const ys: number[] = [];
  const zs: number[] = [];
  for (const n of niveluri) {
    for (const [x, y] of n.poly) {
      xs.push(x);
      ys.push(y);
    }
    zs.push(n.z, n.z + n.h);
  }
  const mn = (a: number[]) => Math.min(...a);
  const mx = (a: number[]) => Math.max(...a);
  const c: [number, number, number] = [
    (mn(xs) + mx(xs)) / 2, (mn(ys) + mx(ys)) / 2, (mn(zs) + mx(zs)) / 2];
  const span = Math.max(mx(xs) - mn(xs), mx(ys) - mn(ys), mx(zs) - mn(zs)) || 1;
  const pos: [number, number, number] = [
    c[0] + span * CAMERA.ofs[0], c[1] + span * CAMERA.ofs[1], c[2] + span * CAMERA.ofs[2]];

  // baza camerei: înainte f (spre țintă), dreapta r = f × up, sus u = r × f
  const norm = (v: number[]) => {
    const l = Math.hypot(v[0], v[1], v[2]) || 1;
    return [v[0] / l, v[1] / l, v[2] / l];
  };
  const cross = (a: number[], b: number[]) => [
    a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
  const f = norm([c[0] - pos[0], c[1] - pos[1], c[2] - pos[2]]);
  const r = norm(cross(f, [0, 0, 1]));
  const u = cross(r, f);
  // fov-ul three.js e VERTICAL: focala în pixeli iese din înălțimea ecranului
  const focala = h / 2 / Math.tan((CAMERA.fov_deg * Math.PI) / 360);

  return {
    P: (x, y, z) => {
      const d = [x - pos[0], y - pos[1], z - pos[2]];
      const xc = d[0] * r[0] + d[1] * r[1] + d[2] * r[2];
      const yc = d[0] * u[0] + d[1] * u[1] + d[2] * u[2];
      const zc = d[0] * f[0] + d[1] * f[1] + d[2] * f[2] || 1e-9;
      return [w / 2 + (xc / zc) * focala, h / 2 - (yc / zc) * focala];
    },
    adancime: (x, y, z) => (x - pos[0]) ** 2 + (y - pos[1]) ** 2 + (z - pos[2]) ** 2,
    pozitie: pos,
  };
}

// --- stâlpii: o grilă în interiorul amprentei ------------------------------
//
// Nu colțurile siluetei: un contur cu bovindou are colțuri în consolă, iar un
// stâlp care plutește deasupra parterului arată ca o greșeală. O grilă de 4×2
// în cutia amprentei, ținând doar punctele care CAD ÎN contur, dă 8 stâlpi pe
// un dreptunghi și mai puțini pe o formă în L — dar niciodată în aer.

export function stalpi(poly: [number, number][], nx = 4, ny = 2): Punct[] {
  if (poly.length < 3) return [];
  const xs = poly.map((p) => p[0]);
  const ys = poly.map((p) => p[1]);
  const x0 = Math.min(...xs);
  const x1 = Math.max(...xs);
  const y0 = Math.min(...ys);
  const y1 = Math.max(...ys);
  // grila e pe latura lungă cu `nx`, pe cea scurtă cu `ny`
  const lung = x1 - x0 >= y1 - y0;
  const [cx, cy] = lung ? [nx, ny] : [ny, nx];
  const out: Punct[] = [];
  for (let i = 0; i < cx; i += 1) {
    for (let j = 0; j < cy; j += 1) {
      // retras cu o jumătate de pas de la margine: stâlpul stă în câmp, nu pe
      // linia conturului, unde jumătate din el ar ieși din clădire
      const x = x0 + ((i + 0.5) / cx) * (x1 - x0);
      const y = y0 + ((j + 0.5) / cy) * (y1 - y0);
      if (inContur([x, y], poly)) out.push([x, y]);
    }
  }
  // o formă în care grila prinde prea puțin (o fâșie diagonală): colțurile
  if (out.length < 4) return poly.slice(0, 8) as Punct[];
  return out;
}

export function inContur([px, py]: Punct, poly: [number, number][]): boolean {
  let inauntru = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i, i += 1) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    if (yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) {
      inauntru = !inauntru;
    }
  }
  return inauntru;
}

// --- fețele plăcii care se văd din cameră ----------------------------------

export type Fata = { a: [number, number]; c: [number, number]; adancime: number };

/** Laturile conturului a căror normală exterioară e întoarsă spre cameră,
 *  de la cea mai depărtată la cea mai apropiată (pictorul). Conturul e în
 *  sens trigonometric (`silueta._ccw`), deci normala laturii a→c e
 *  (dy, −dx). */
export function feteVizibile(poly: [number, number][], z: number, cam: Camera): Fata[] {
  const out: Fata[] = [];
  for (let i = 0; i < poly.length; i += 1) {
    const a = poly[i];
    const c = poly[(i + 1) % poly.length];
    const nx = c[1] - a[1];
    const ny = -(c[0] - a[0]);
    const mx = (a[0] + c[0]) / 2;
    const my = (a[1] + c[1]) / 2;
    const spre = (cam.pozitie[0] - mx) * nx + (cam.pozitie[1] - my) * ny;
    if (spre <= 0) continue;
    out.push({ a, c, adancime: cam.adancime(mx, my, z) });
  }
  return out.sort((p, q) => q.adancime - p.adancime);
}

// --- ce arată Studioul când nu are modelul ---------------------------------
//
// Rezumatele pașilor sosesc înaintea rezultatelor lor (StructureContext aduce
// întâi lista scurtă, apoi fiecare rezultat mare), iar un pas cu rezultat pe
// server dar neadus încă NU intră în `steps` (mergeSteps). Deci lipsa lui din
// `steps` înseamnă două lucruri diferite, și numai rezumatul le desparte.
export type StareModel = "gata" | "se_incarca" | "lipseste";

export function stareModel(
  pas: StepState | undefined,
  rezumate: Record<string, StepRezumat> | null | undefined,
): StareModel {
  if (pas?.result && (pas.result as { model?: unknown }).model) return "gata";
  // rezumatele n-au sosit încă: nu știm nimic, deci nu afirmăm că lipsește
  if (rezumate == null) return "se_incarca";
  if (rezumate["model_fem"]?.are_rezultat) return "se_incarca";
  return "lipseste";
}
